/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  TextField,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import PowerIcon from '@mui/icons-material/Power';
import TableChartIcon from '@mui/icons-material/TableChart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  getIntegrationByAlias,
  getIntegrationObjects,
  getIntegrationObjectRows,
  normalizeIntegration,
  normalizeObjects,
  normalizeRows,
  integrationErrorMessage,
} from '../api/integrationsApi';
import { environment } from '../../environments/environment';
import { dsVar, ink } from '../theme/tokens';

const ROW_LIMIT = 10; // rows per page
const RAIL_WIDTH = 280;
// Wide text columns (notes, descriptions) would otherwise consume the whole
// viewport and push every other column off-screen, so cap them and let the
// title tooltip carry the full value.
const CELL_MAX_WIDTH = 150;
const CELL_MAX_CHARS = 40;
const FILTER_THRESHOLD = 10;

const BG_DEEP = dsVar('page');
const BG_CARD = dsVar('surface');
const BG_HEADER = dsVar('raised');
const BG_FIELD = dsVar('raised');
const BORDER = dsVar('line');
const TEXT_BODY = dsVar('fg-body');
const TEXT_MUTED = dsVar('fg-2');
const TEXT_FAINT = dsVar('fg-muted');

const MONO = "'JetBrains Mono', monospace";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;

const humanizeKey = (key) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isDateType = (dataType) => !!dataType && /date|timestamp/i.test(dataType);

/** `label` (e.g. a Drive file's display name) is shown when present; `name` — the stable identifier used for API calls — is the fallback. */
const objectDisplayName = (obj) => obj?.label || obj?.name || '';

/**
 * The activity template names its bound object without an environment suffix
 * (`case_contexts`), while the source database carries per-environment copies
 * (`case_contexts_dev`). Prefer the suffixed name, then fall back to the bare
 * one so environments that do not suffix their tables still resolve.
 */
const defaultObjectCandidates = (objectName) =>
  objectName ? [`${objectName}_${environment.name}`, objectName] : [];

/** Shared loading / error / empty pane so no pane can ever render blank. */
const PaneState = ({ loading, error, empty, emptyText, accent, onRetry, children }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} style={{ color: accent }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          padding: '16px',
          backgroundColor: BG_FIELD,
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: '8px',
        }}
      >
        <ErrorOutlineIcon sx={{ color: ink('#EF4444'), fontSize: 20, mt: '2px' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: TEXT_BODY, fontSize: '13px', fontWeight: 600, mb: 0.5 }}>
            Could not load this data
          </Typography>
          <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', wordBreak: 'break-word' }}>
            {error}
          </Typography>
        </Box>
        {onRetry && (
          <IconButton
            size="small"
            onClick={onRetry}
            sx={{ color: accent, border: `1px solid ${accent}`, borderRadius: '8px', px: 1.5 }}
          >
            <RefreshIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>Retry</Typography>
          </IconButton>
        )}
      </Box>
    );
  }
  if (empty) {
    return (
      <Typography sx={{ color: TEXT_MUTED, fontSize: '13px', py: 4, textAlign: 'center' }}>
        {emptyText}
      </Typography>
    );
  }
  return children;
};

/** Renders one cell value according to its declared column type. */
const renderCell = (value, dataType) => {
  if (value === null || value === undefined) {
    return <span style={{ color: TEXT_FAINT, fontStyle: 'italic' }}>null</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <Chip
        label={String(value)}
        size="small"
        sx={{
          height: '18px',
          fontSize: '10px',
          fontFamily: MONO,
          color: value ? ink('#10B981') : TEXT_MUTED,
          backgroundColor: value ? 'rgba(16,185,129,0.15)' : dsVar('fg-2', 0.12),
        }}
      />
    );
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return (
      <span title={json} style={{ fontFamily: MONO, fontSize: '11px' }}>
        {json.length > CELL_MAX_CHARS ? `${json.slice(0, CELL_MAX_CHARS)}…` : json}
      </span>
    );
  }
  const str = String(value);
  if (typeof value === 'string' && (isDateType(dataType) || ISO_DATE.test(str))) {
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return <span title={str}>{parsed.toLocaleDateString()}</span>;
    }
  }
  if (str.length > CELL_MAX_CHARS) {
    return <span title={str}>{`${str.slice(0, CELL_MAX_CHARS)}…`}</span>;
  }
  return str;
};

/** Prev/Next control matching the marketplace pagination styling. */
const PagerButton = ({ accent, disabled, onClick, children }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 0.75,
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: 700,
      backgroundColor: 'transparent',
      border: `1px solid ${disabled ? dsVar('fg', 0.1) : `${accent}80`}`,
      color: disabled ? dsVar('fg', 0.3) : accent,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 0.15s ease',
      '&:hover': { backgroundColor: disabled ? 'transparent' : `${accent}1A` },
    }}
  >
    {children}
  </Box>
);

const railItemSx = (selected, accent) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: '8px 12px',
  borderLeft: `3px solid ${selected ? accent : 'transparent'}`,
  backgroundColor: selected ? `${accent}14` : 'transparent',
  color: selected ? accent : TEXT_BODY,
  cursor: 'pointer',
  fontSize: '13px',
  transition: 'background-color 0.15s ease',
  '&:hover': { backgroundColor: selected ? `${accent}14` : 'rgba(148,163,184,0.08)' },
});

const microLabelSx = {
  color: TEXT_MUTED,
  fontSize: '10px',
  fontWeight: 600,
  fontFamily: MONO,
  textTransform: 'uppercase',
  letterSpacing: '-0.02em',
};

/**
 * Read-only master/detail explorer for the data source bound to a flow node.
 *   left rail  → pinned Connection entry + object (table) list
 *   right pane → connection details, or the selected object's Data / Schema tabs
 * Opened from IntegrationBindingCard; layers over the node edit drawer, which
 * stays mounted underneath so closing returns the user where they were.
 */
const IntegrationExplorerDrawer = ({
  open,
  onClose,
  integrationAlias,
  defaultObjectName,
  accent = '#FFDE59',
}) => {
  const [integration, setIntegration] = useState(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);

  const [objects, setObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [objectsError, setObjectsError] = useState(null);

  const [selectedObject, setSelectedObject] = useState(null); // null => Connection view
  const [objectData, setObjectData] = useState(null);
  const [objectLoading, setObjectLoading] = useState(false);
  const [objectError, setObjectError] = useState(null);
  const [objectTab, setObjectTab] = useState('data');
  const [objectPage, setObjectPage] = useState(1);

  const [filter, setFilter] = useState('');

  // Cached for the drawer's lifetime so pivoting between objects does not
  // re-query the customer's database on every click.
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);

  const newSignal = () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  };

  const loadIntegration = useCallback(async () => {
    if (!integrationAlias) return;
    const cacheKey = `integration:${integrationAlias}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setIntegration(cached);
      return cached;
    }
    setIntegrationLoading(true);
    setIntegrationError(null);
    try {
      const res = await getIntegrationByAlias(integrationAlias, { signal: newSignal() });
      const normalized = normalizeIntegration(res);
      cacheRef.current.set(cacheKey, normalized);
      setIntegration(normalized);
      return normalized;
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return null;
      setIntegrationError(integrationErrorMessage(error, 'Unable to load the integration.'));
      return null;
    } finally {
      setIntegrationLoading(false);
    }
  }, [integrationAlias]);

  const loadObjects = useCallback(async (integrationId) => {
    if (!integrationId) return [];
    const cacheKey = `objects:${integrationId}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setObjects(cached);
      return cached;
    }
    setObjectsLoading(true);
    setObjectsError(null);
    try {
      const res = await getIntegrationObjects(integrationId, { signal: newSignal() });
      const normalized = normalizeObjects(res);
      cacheRef.current.set(cacheKey, normalized);
      setObjects(normalized);
      return normalized;
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return [];
      setObjectsError(integrationErrorMessage(error, 'Unable to list objects in this data source.'));
      return [];
    } finally {
      setObjectsLoading(false);
    }
  }, []);

  const loadObjectRows = useCallback(async (integrationId, objectName, page = 1) => {
    const isFirstPage = page === 1;
    const cacheKey = `rows:${integrationId}:${objectName}:${page}`;
    setObjectPage(page);
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setObjectData(cached);
      if (isFirstPage) setObjectTab(cached.rows.length ? 'data' : 'schema');
      return;
    }
    setObjectLoading(true);
    setObjectError(null);
    // Keep the current page rendered while stepping to the next one, so the
    // table does not collapse and reflow on every click.
    if (isFirstPage) setObjectData(null);
    try {
      const res = await getIntegrationObjectRows(integrationId, objectName, {
        limit: ROW_LIMIT,
        page,
        signal: newSignal(),
      });
      const normalized = normalizeRows(res);
      cacheRef.current.set(cacheKey, normalized);
      setObjectData(normalized);
      // With no rows the horizontal preview is useless — the schema is the
      // only thing worth showing, so land there instead. Only on first load;
      // paging past the end must not yank the user onto another tab.
      if (isFirstPage) setObjectTab(normalized.rows.length ? 'data' : 'schema');
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      setObjectError(integrationErrorMessage(error, 'Unable to preview this object.'));
    } finally {
      setObjectLoading(false);
    }
  }, []);

  // A different binding is a different data source — drop every cached response.
  // Declared before the loader effect so a fresh alias is always reset first.
  useEffect(() => {
    cacheRef.current.clear();
    setIntegration(null);
    setObjects([]);
    setSelectedObject(null);
    setObjectData(null);
    setObjectPage(1);
    setFilter('');
    setIntegrationError(null);
    setObjectsError(null);
    setObjectError(null);
  }, [integrationAlias]);

  // Lazy: nothing is fetched until the drawer is actually opened. Once the
  // object list is in, land directly on the node's bound object (if the
  // activity template named one) instead of the Connection view, so the
  // records the user actually came here for are one open away, not two.
  useEffect(() => {
    if (!open || !integrationAlias) return;
    let cancelled = false;
    (async () => {
      const loaded = await loadIntegration();
      if (cancelled || !loaded?._id) return;
      const loadedObjects = await loadObjects(loaded._id);
      if (cancelled) return;
      const match = defaultObjectCandidates(defaultObjectName).find((name) =>
        loadedObjects.some((o) => o.name === name)
      );
      if (match) {
        setSelectedObject(match);
        setObjectPage(1);
        loadObjectRows(loaded._id, match, 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, integrationAlias, defaultObjectName, loadIntegration, loadObjects, loadObjectRows]);

  // Abort anything in flight when the drawer closes so a slow query cannot
  // resolve into a closed surface.
  useEffect(() => {
    if (!open) abortRef.current?.abort();
  }, [open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleSelectObject = (name) => {
    setSelectedObject(name);
    setObjectPage(1);
    if (integration?._id) loadObjectRows(integration._id, name, 1);
  };

  const goToPage = (page) => {
    if (integration?._id && selectedObject) loadObjectRows(integration._id, selectedObject, page);
  };

  const filteredObjects = filter
    ? objects.filter((o) => {
        const term = filter.toLowerCase();
        return o.name.toLowerCase().includes(term) || o.label?.toLowerCase().includes(term);
      })
    : objects;

  const selectedObjectEntry = objects.find((o) => o.name === selectedObject) || null;

  const connectionEntries = Object.entries(integration?.connection || {});

  const renderConnectionPane = () => (
    <Box>
      <Typography sx={{ ...microLabelSx, mb: 2 }}>Connection</Typography>
      <PaneState
        loading={integrationLoading}
        error={integrationError}
        empty={!integrationLoading && !integrationError && connectionEntries.length === 0}
        emptyText="This integration has no connection details to display."
        accent={accent}
        onRetry={loadIntegration}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '16px 32px',
          }}
        >
          {connectionEntries.map(([key, value]) => {
            const isComplex = value !== null && typeof value === 'object';
            return (
              <Box key={key} sx={{ gridColumn: isComplex ? '1 / -1' : 'auto', minWidth: 0 }}>
                <Typography sx={{ ...microLabelSx, mb: 0.5 }}>{humanizeKey(key)}</Typography>
                {isComplex ? (
                  <Box
                    component="pre"
                    sx={{
                      margin: 0,
                      padding: '10px 12px',
                      backgroundColor: BG_FIELD,
                      border: `1px solid ${BORDER}`,
                      borderRadius: '6px',
                      color: TEXT_BODY,
                      fontFamily: MONO,
                      fontSize: '11px',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify(value, null, 2)}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      color: TEXT_BODY,
                      fontSize: '13px',
                      fontFamily: MONO,
                      wordBreak: 'break-word',
                    }}
                  >
                    {value === null || value === undefined || value === '' ? '—' : String(value)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </PaneState>
    </Box>
  );

  const renderObjectPane = () => {
    const columns = objectData?.columns || [];
    const rows = objectData?.rows || [];
    const pageInfo = objectData?.pagination || null;
    // The endpoint pages with ?page&limit but is not guaranteed to return the
    // { pagination } envelope. With it we can show "Page 2 of 47"; without it
    // a full page is the only signal that more rows may exist.
    const totalPages = pageInfo?.pages || null;
    const hasPrev = objectPage > 1;
    const hasNext = totalPages ? objectPage < totalPages : rows.length === ROW_LIMIT;
    const showPager = objectTab === 'data' && !!objectData && (hasPrev || hasNext);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['data', 'schema'].map((tab) => (
              <Box
                key={tab}
                onClick={() => setObjectTab(tab)}
                sx={{
                  padding: '5px 14px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  color: objectTab === tab ? dsVar('on-accent') : TEXT_MUTED,
                  backgroundColor: objectTab === tab ? accent : BG_FIELD,
                  border: `1px solid ${objectTab === tab ? accent : BORDER}`,
                }}
              >
                {tab}
              </Box>
            ))}
          </Box>
          {!!objectData && (
            <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', fontFamily: MONO }}>
              {pageInfo?.total != null
                ? `${pageInfo.total.toLocaleString()} ${pageInfo.total === 1 ? 'row' : 'rows'}`
                : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'} on this page`}{' '}
              · {columns.length} {columns.length === 1 ? 'column' : 'columns'}
            </Typography>
          )}
        </Box>

        <PaneState
          loading={objectLoading && !objectData}
          error={objectError}
          empty={!objectLoading && !objectError && !objectData}
          emptyText="No preview available for this object."
          accent={accent}
          onRetry={() => goToPage(objectPage)}
        >
          {objectTab === 'data' ? (
            <>
            {rows.length === 0 ? (
              <Typography sx={{ color: TEXT_MUTED, fontSize: '13px', py: 4, textAlign: 'center' }}>
                {objectPage > 1 ? 'No more rows.' : 'This object has no rows.'}
              </Typography>
            ) : (
              <TableContainer
                  sx={{
                    backgroundColor: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                    overflowX: 'auto',
                    maxHeight: '60vh',
                    opacity: objectLoading ? 0.5 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <Table size="small" stickyHeader sx={{ width: 'max-content', minWidth: '100%' }}>
                    <TableHead>
                      <TableRow>
                        {columns.map((col, idx) => (
                          <TableCell
                            key={col.name}
                            title={col.name}
                            sx={{
                              backgroundColor: BG_HEADER,
                              borderBottom: `1px solid ${BORDER}`,
                              whiteSpace: 'nowrap',
                              padding: '6px 10px',
                              maxWidth: CELL_MAX_WIDTH,
                              // Keep the identity column visible while the user
                              // scrolls through wide tables.
                              ...(idx === 0 && {
                                position: 'sticky',
                                left: 0,
                                zIndex: 4,
                                borderRight: `1px solid ${BORDER}`,
                              }),
                            }}
                          >
                            <Typography
                              sx={{
                                color: accent,
                                fontFamily: MONO,
                                fontSize: '12px',
                                fontWeight: 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {col.name}
                              {col.nullable === false && (
                                <span style={{ color: ink('#EF4444'), marginLeft: 3 }}>*</span>
                              )}
                            </Typography>
                            {col.dataType && (
                              <Typography sx={{ color: TEXT_FAINT, fontSize: '10px', fontFamily: MONO }}>
                                {col.dataType}
                              </Typography>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, rowIdx) => (
                        <TableRow key={rowIdx} hover>
                          {columns.map((col, idx) => (
                            <TableCell
                              key={col.name}
                              sx={{
                                color: TEXT_BODY,
                                fontSize: '12px',
                                borderBottom: `1px solid ${BORDER}`,
                                whiteSpace: 'nowrap',
                                padding: '6px 10px',
                                maxWidth: CELL_MAX_WIDTH,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                ...(idx === 0 && {
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 2,
                                  backgroundColor: BG_CARD,
                                  borderRight: `1px solid ${BORDER}`,
                                }),
                              }}
                            >
                              {renderCell(row?.[col.name], col.dataType)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              </TableContainer>
            )}
            {showPager && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                }}
              >
                <PagerButton
                  accent={accent}
                  disabled={!hasPrev || objectLoading}
                  onClick={() => goToPage(objectPage - 1)}
                >
                  <ArrowBackIcon sx={{ fontSize: 15, mr: 0.75 }} />
                  Previous
                </PagerButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      color: TEXT_MUTED,
                      fontSize: '12px',
                      fontWeight: 700,
                      fontFamily: MONO,
                      borderLeft: `1px solid ${BORDER}`,
                      borderRight: `1px solid ${BORDER}`,
                      px: 2,
                      py: 0.5,
                    }}
                  >
                    Page {objectPage}
                    {totalPages ? ` of ${totalPages}` : ''}
                  </Typography>
                  {objectLoading && <CircularProgress size={13} style={{ color: accent }} />}
                </Box>
                <PagerButton
                  accent={accent}
                  disabled={!hasNext || objectLoading}
                  onClick={() => goToPage(objectPage + 1)}
                >
                  Next
                  <ArrowForwardIcon sx={{ fontSize: 15, ml: 0.75 }} />
                </PagerButton>
              </Box>
            )}
            </>
          ) : (
            <TableContainer
              sx={{
                backgroundColor: BG_CARD,
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                maxHeight: '65vh',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Column', 'Type', 'Nullable', 'Has default'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: BG_HEADER,
                          color: accent,
                          fontFamily: MONO,
                          fontSize: '12px',
                          fontWeight: 700,
                          borderBottom: `1px solid ${BORDER}`,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {columns.map((col) => (
                    <TableRow key={col.name} hover>
                      <TableCell
                        sx={{
                          color: TEXT_BODY,
                          fontFamily: MONO,
                          fontSize: '12px',
                          borderBottom: `1px solid ${BORDER}`,
                        }}
                      >
                        {col.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: TEXT_MUTED,
                          fontFamily: MONO,
                          fontSize: '12px',
                          borderBottom: `1px solid ${BORDER}`,
                        }}
                      >
                        {col.dataType || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: '12px' }}>
                        {col.nullable === null ? (
                          <span style={{ color: TEXT_FAINT }}>—</span>
                        ) : (
                          renderCell(col.nullable)
                        )}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: '12px' }}>
                        {col.hasDefault === null ? (
                          <span style={{ color: TEXT_FAINT }}>—</span>
                        ) : (
                          renderCell(col.hasDefault)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </PaneState>
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: 'min(1600px, 96vw)' },
          backgroundColor: BG_DEEP,
          color: TEXT_BODY,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          padding: '16px 24px',
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}
      >
        <StorageIcon sx={{ color: accent, fontSize: 20 }} />
        <Typography sx={{ color: accent, fontFamily: MONO, fontSize: '15px', fontWeight: 700 }}>
          {integrationAlias}
        </Typography>
        {integration?.provider && (
          <Chip
            label={integration.provider}
            size="small"
            sx={{
              height: '20px',
              fontSize: '10px',
              fontFamily: MONO,
              color: TEXT_MUTED,
              backgroundColor: BG_FIELD,
              border: `1px solid ${BORDER}`,
            }}
          />
        )}
        {integration?.status && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: integration.status === 'active' ? '#10B981' : TEXT_MUTED,
              }}
            />
            <Typography sx={{ color: TEXT_MUTED, fontSize: '11px' }}>{integration.status}</Typography>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {integration?._id && (
          <Typography sx={{ color: TEXT_FAINT, fontSize: '10px', fontFamily: MONO }}>
            {integration._id}
          </Typography>
        )}
        <IconButton onClick={onClose} sx={{ color: TEXT_MUTED }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left rail */}
        <Box
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${BORDER}`,
            backgroundColor: BG_CARD,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            onClick={() => setSelectedObject(null)}
            sx={{ ...railItemSx(selectedObject === null, accent), mt: 1.5 }}
          >
            <PowerIcon sx={{ fontSize: 16 }} />
            Connection
          </Box>

          <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: 1.5, pt: 1.5, px: 1.5 }}>
            <Typography sx={microLabelSx}>Available Datasets ({objects.length})</Typography>
          </Box>

          {objects.length > FILTER_THRESHOLD && (
            <Box sx={{ px: 1.5, pt: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter objects"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    color: TEXT_BODY,
                    fontSize: '13px',
                    backgroundColor: BG_FIELD,
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accent },
                }}
              />
            </Box>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto', mt: 1, minHeight: 0 }}>
            <PaneState
              loading={objectsLoading}
              error={objectsError}
              empty={!objectsLoading && !objectsError && filteredObjects.length === 0}
              emptyText={
                objects.length === 0
                  ? 'No objects found in this data source.'
                  : 'No objects match that filter.'
              }
              accent={accent}
              onRetry={() => integration?._id && loadObjects(integration._id)}
            >
              {filteredObjects.map((obj) => {
                const displayName = objectDisplayName(obj);
                // Surface the underlying identifier in the tooltip whenever a
                // label hides it, so it's still reachable (e.g. to distinguish
                // same-named files, or to match against the activity template).
                const tooltip = obj.label && obj.label !== obj.name
                  ? `${displayName} (${obj.name})`
                  : obj.schema
                    ? `${obj.schema}.${obj.name}`
                    : obj.name;
                return (
                  <Box
                    key={`${obj.schema || ''}.${obj.name}`}
                    onClick={() => handleSelectObject(obj.name)}
                    sx={railItemSx(selectedObject === obj.name, accent)}
                    title={tooltip}
                  >
                    <TableChartIcon sx={{ fontSize: 15, flexShrink: 0, opacity: 0.7 }} />
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: MONO,
                        fontSize: '12.5px',
                      }}
                    >
                      {displayName}
                    </Box>
                  </Box>
                );
              })}
            </PaneState>
          </Box>
        </Box>

        {/* Right pane */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '24px' }}>
          {selectedObject && (
            <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', fontFamily: MONO, mb: 2 }}>
              {integrationAlias} <span style={{ color: TEXT_FAINT }}>›</span>{' '}
              <span style={{ color: TEXT_BODY }}>
                {selectedObjectEntry ? objectDisplayName(selectedObjectEntry) : selectedObject}
              </span>
            </Typography>
          )}
          {selectedObject === null ? renderConnectionPane() : renderObjectPane()}
        </Box>
      </Box>
    </Drawer>
  );
};

export default IntegrationExplorerDrawer;
